import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Sun,
  Moon,
  Bell,
  Menu,
  PanelLeft,
  PanelLeftClose,
  User,
  CheckCircle,
  Loader2,
  ScrollText,
  AlertTriangle,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import useAuth from "../../hooks/useAuth";
import { motion, AnimatePresence } from "framer-motion";
import { notificationService } from "../../api/notificationService";
import { useDevRulesLock } from "../../context/DevRulesLockProvider";
import NavbarClock from "./NavbarClock";

// Breadcrumb "Section / Page" by path prefix (longest match wins).
const CRUMBS = [
  ["/main/dashboard", "Main", "Dashboard"],
  ["/main/week-schedule", "Main", "Week Schedule"],
  ["/main/assigned-to-you", "Main", "Assigned to You"],
  ["/main/documentation", "Main", "Documentation"],
  ["/main/dev-rules", "Main", "Dev Rules"],
  ["/task-board/projects", "Task Management", "Projects"],
  ["/task-board/squads", "Task Management", "Squads"],
  ["/task-board/sprints", "Task Management", "Sprints"],
  ["/task-board/employee-requests", "Task Management", "Employee Requests"],
  ["/task-board", "Task Management", "Task Board"],
  ["/ai-studio/qa-engineer", "AI Studio", "QA Engineer"],
  ["/ai-studio", "AI Studio", "Workspace"],
  ["/management/employees", "Management", "Employees"],
  ["/management/hr-form", "Management", "HR Form"],
  ["/management/requests", "Management", "Requests"],
  ["/management/attendance/admin", "Management", "All Attendance"],
  ["/management/attendance", "Management", "Attendance"],
  ["/management/monthly-report", "Management", "Monthly Report"],
  ["/management/monitoring", "Management", "Monitoring"],
  ["/management/system-status", "Management", "System Status"],
  ["/team-presence", "Management", "Team Presence"],
  ["/communication/tickets", "Communication", "Tickets"],
  ["/communication/chat", "Communication", "Chat"],
  ["/communication/meeting-notes", "Communication", "Notes"],
  ["/communication/expenses", "Communication", "Expenses"],
  ["/notifications", "Account", "Notifications"],
  ["/profile", "Account", "Profile"],
];
const crumbFor = (path) =>
  CRUMBS.filter(([p]) => path === p || path.startsWith(p + "/")).sort((a, b) => b[0].length - a[0].length)[0] ||
  null;

const iconBtn =
  "relative inline-flex items-center justify-center w-10 h-10 rounded-xl border border-line2 bg-panel text-muted hover:text-ink hover:bg-hover transition-colors";

function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60)
    return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString();
}

function getNotificationIcon(type) {
  const t = (type || "").toLowerCase();
  if (
    t.includes("task") ||
    t === "task_assigned" ||
    t === "task_wait_to_review"
  ) {
    return {
      Icon: Bell,
      iconClass:
        "bg-grad text-on-accent",
    };
  }
  if (t.includes("admin") || t === "team") {
    return {
      Icon: User,
      iconClass:
        "bg-info-soft text-info",
    };
  }
  if (t === "success" || t.includes("complete")) {
    return {
      Icon: CheckCircle,
      iconClass:
        "bg-ok-soft text-ok",
    };
  }
  return {
    Icon: Bell,
    iconClass: "bg-grad text-on-accent",
  };
}

const Navbar = ({ isMobile = false, isImmersive = false }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const crumb = crumbFor(location.pathname);
  const { auth, employee } = useAuth();
  const { lockState } = useDevRulesLock();
  // Use employee from context (cached or fetched); fallback to auth.employee for immediate display
  const profile = employee ?? auth?.employee ?? null;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [showNotifications, setShowNotifications] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const notificationsRef = useRef(null);

  const fetchRecentNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const [listRes, countRes] = await Promise.all([
        notificationService.getNotifications({ limit: 3, page: 1 }),
        notificationService.getUnreadCount(),
      ]);
      setRecentNotifications(listRes.data || []);
      setUnreadCount(countRes?.data?.count ?? 0);
    } catch (err) {
      setRecentNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    if (showNotifications) fetchRecentNotifications();
  }, [showNotifications, fetchRecentNotifications]);

  useEffect(() => {
    if (!profile?.id && !profile?._id) return;
    notificationService
      .getUnreadCount()
      .then((res) => {
        setUnreadCount(res?.data?.count ?? 0);
      })
      .catch(() => {});
  }, [profile?.id, profile?._id]);

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setRecentNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("Mark all as read failed:", err);
    }
  };

  const hasRulesUpdate = Boolean(lockState?.locked);

  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener("sidebarCollapse", handleSidebarCollapse);
    return () =>
      window.removeEventListener("sidebarCollapse", handleSidebarCollapse);
  }, []);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showNotifications]);

  const toggleSidebar = () => {
    window.dispatchEvent(new CustomEvent("toggleSidebar"));
  };

  return (
    <header
      className="sticky top-0 z-[45] h-16 px-3 sm:px-4 md:px-7 flex items-center justify-between gap-3 border-b border-line backdrop-blur-[20px] transition-transform duration-[400ms]"
      style={{
        background: "color-mix(in srgb, var(--bg) 45%, transparent)",
        transform: isImmersive ? "translateY(-140%)" : "translateY(0)",
        pointerEvents: isImmersive ? "none" : undefined,
      }}
    >
      {/* Left: sidebar toggle + breadcrumb */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={toggleSidebar}
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl text-muted hover:text-ink hover:bg-hover transition-colors touch-manipulation"
          title={isMobile ? "Open menu" : sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={isMobile ? "Open menu" : sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isMobile ? (
            <Menu className="w-[18px] h-[18px]" />
          ) : sidebarCollapsed ? (
            <PanelLeft className="w-[18px] h-[18px]" />
          ) : (
            <PanelLeftClose className="w-[18px] h-[18px]" />
          )}
        </button>
        {crumb && (
          <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-2 text-[13.5px] whitespace-nowrap min-w-0">
            <span className="text-faint">{crumb[1]}</span>
            <span className="text-faint">/</span>
            <span className="text-ink font-medium truncate">{crumb[2]}</span>
          </nav>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        <NavbarClock />
        <button
          onClick={() => navigate("/main/dev-rules")}
          className="btn-ghost !h-10 !px-3.5 text-[13.5px] text-muted hover:text-ink"
          title="Dev Rules"
        >
          <ScrollText className="w-4 h-4" />
          <span className="hidden md:inline">Dev Rules</span>
          {hasRulesUpdate && <AlertTriangle className="w-4 h-4 text-accent" />}
        </button>

        {/* Theme Toggle */}
        <button
          onClick={(e) => toggleTheme(e)}
          className={iconBtn}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
        >
          {theme === "dark" ? (
            <Sun className="w-[18px] h-[18px]" />
          ) : (
            <Moon className="w-[18px] h-[18px]" />
          )}
        </button>

        {/* Notifications Center */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={iconBtn}
            aria-label="Notifications"
          >
            <Bell className="w-[18px] h-[18px]" />
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-grad rounded-full ring-2 ring-bg" />
            )}
          </button>

          {/* Notifications Dropdown */}
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="fixed left-3 right-3 top-16 sm:absolute sm:fixed-auto sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-panel-solid border border-line2 rounded-2xl shadow-modal overflow-hidden z-50"
              >
                {/* Notifications Header */}
                <div className="px-4 py-3 border-b border-line flex items-center justify-between">
                  <h3 className="font-display text-[15px] font-semibold text-ink">
                    Notifications
                  </h3>
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                    className="text-xs text-accent-ink hover:text-accent font-medium disabled:opacity-50 disabled:pointer-events-none"
                  >
                    Mark all as read
                  </button>
                </div>

                {/* Last 3 notifications (newest first) */}
                <div className="max-h-[65vh] sm:max-h-[400px] overflow-y-auto">
                  {loadingNotifications ? (
                    <div className="p-6 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-accent" />
                    </div>
                  ) : recentNotifications.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500 dark:text-light-500">
                      No notifications yet
                    </div>
                  ) : (
                    recentNotifications.map((notification) => {
                      const { Icon, iconClass } = getNotificationIcon(
                        notification.data?.type,
                      );
                      const unread = !notification.read;
                      return (
                        <div
                          key={notification._id}
                          onClick={() => {
                            if (notification.data?.url)
                              navigate(notification.data.url);
                            setShowNotifications(false);
                          }}
                          className="p-4 border-b border-line hover:bg-hover transition-colors cursor-pointer"
                        >
                          <div className="flex gap-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconClass}`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-light-100 truncate">
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-light-500 mt-1 line-clamp-2">
                                {notification.body || notification.title}
                              </p>
                              <p
                                className={`text-xs mt-1 ${unread ? "text-primary-accent" : "text-gray-400 dark:text-light-500"}`}
                              >
                                {formatRelativeTime(notification.createdAt)}
                              </p>
                            </div>
                            {unread && (
                              <div className="w-2 h-2 bg-primary-accent rounded-full flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* View All Footer */}
                <div className="p-3 text-center">
                  <button
                    onClick={() => {
                      navigate("/notifications");
                      setShowNotifications(false);
                    }}
                    className="text-sm font-medium text-accent-ink hover:text-accent transition-colors"
                  >
                    View all notifications
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </header>
  );
};

export default Navbar;
